/**
 * /de/comparisons/notion-ai — Draftlize vs Notion (German). Links to
 * /de/entscheidungsprotokoll.
 */
import { MarketingShell } from '../MarketingShell';

export function NotionAiDe() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/comparisons/notion-ai"
      hreflangLocales={['de']}
      documentTitle="Notion-Alternative fürs Produkt: Draftlize vs Notion · Draftlize"
      documentDescription="Notion speichert Seiten; Draftlize ist ein Karten-Graph, den ein KI-Agent liest und schreibt, damit Entscheidungen nicht driften. Direkter Vergleich."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Vergleich · vs Notion</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Draftlize vs Notion:<br />
            <em>Was passt zu deiner Produktarbeit?</em>
          </h1>
          <p className="lede">
            Kurz gesagt: Notion ist das beste Werkzeug, um Dinge aufzuschreiben. Draftlize ist ein Substrat, in dem KI-Agenten dein Produktdenken als strukturierte Karten <em>lesen und schreiben</em> können. Sie lösen benachbarte Probleme – es geht weniger darum, das eine durchs andere zu ersetzen, als zu entscheiden, was du gerade wirklich brauchst.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Kurzfassung</span>
            <h2 className="serif">Direkt nebeneinander, <em>7 Dimensionen.</em></h2>
          </div>
          <p>Aus derselben Roadmap und denselben Szenarien, in beiden Tools durchgespielt.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Notion</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Form des Substrats</td><td className="ours yes">Typisierter Karten-Graph</td><td className="partial">Seiten aus Blöcken</td></tr>
            <tr><td>KI liest Entscheidungen jedes Mal als Kontext</td><td className="ours yes">nativ</td><td className="no">—</td></tr>
            <tr><td>KI schreibt als strukturierte Karten zurück</td><td className="ours yes">nativ</td><td className="partial">schreibt Blöcke, ohne Struktur</td></tr>
            <tr><td>Stale-Propagation bei Änderung einer Abhängigkeit</td><td className="ours yes">nativ</td><td className="no">—</td></tr>
            <tr><td>Freie Dokumente und Datenbanken</td><td className="no">—</td><td className="yes">exzellent</td></tr>
            <tr><td>Team-Wiki · Rechte · Zusammenarbeit</td><td className="no">Solo (v1)</td><td className="yes">exzellent</td></tr>
            <tr><td>Claude Code / MCP-Integration</td><td className="ours yes">nativ</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Wo <em>Notion gewinnt.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Wenn du ein Wiki brauchst</h3>
            <p>Notion ist das beste Wiki, das es gibt. Wenn die Frage lautet „Wo liegt das Runbook des Teams?“, dann Notion. Draftlize will kein Wiki sein.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Wenn du in Tabellen und Datenbanken lebst</h3>
            <p>Notions Datenbank- und Ansichtssystem ist unschlagbar. Roadmap-Tracker, Sprint-Raster, Bewertungstabellen: für diese Formen gewinnen Notions strukturierte Collections jedes Mal.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Wenn dein Team groß ist</h3>
            <p>Echtzeit-Zusammenarbeit, feine Rechte, Kommentare, Erwähnungen – dafür ist Notion gebaut. Draftlize ist bewusst Solo-first.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Wo <em>Draftlize gewinnt.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">KI, die sich <em>wirklich</em> erinnert</h3>
            <p>Notion AIs „Gedächtnis“ ist Best-Effort-Retrieval in den Chat. In Draftlize ist das Substrat, in dem der Agent arbeitet, die Karten – also liest er deine Preis-Entscheidung in jedem Chat automatisch. Wieder-Erklären geht gegen null.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Drift wird sichtbar</h3>
            <p>Du änderst eine Notion-Seite → niemand merkt es. Du änderst eine Entscheidungskarte in Draftlize → jede abhängige Karte wird automatisch <code>stale</code>. Das Substrat verfolgt Invalidierung wie ein Build-System.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Erstklassiger Kontext für Claude Code / Cursor / MCP</h3>
            <p>Notion AI lebt in Notion. Draftlize-Karten landen per CLI im Repo und erscheinen via MCP in jedem KI-Tool. Entscheidungen sind aus jedem Tool adressierbar. Weiter zum <a href="/de/entscheidungsprotokoll" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Entscheidungsprotokoll</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Notion ist die Antwort auf „Wo schreibt das Team Dinge auf“. Draftlize ist die Antwort auf „Wo verdichtet sich Produktdenken, wenn KI die Hälfte der Arbeit macht“.
        </blockquote>
        <cite>Wenn du nur die erste Antwort brauchst, reicht Notion.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Gratis mit <em>$5 starten.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Du kannst Notion behalten. Draftlize ist das Substrat unter den KI-gestützten Teilen. $5 Gratis-Guthaben, zahle nur, was du nutzt, Guthaben verfällt nie.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
